import { useState } from "react";
import { ChevronDown } from "lucide-react";

export const faqData = [
  {
    question: "What type of research do you conduct?",
    answer: "We only conduct healthcare research. Specifically, we collect and analyze data about various aspects of the healthcare industry, such as patient experiences, treatment effectiveness, and opinions on new products or services. It helps healthcare providers, pharmaceutical companies, and policymakers make informed decisions to improve patient care and outcomes.\n\nOur studies can take the form of online surveys, interviews, focus groups, clinical trials, observational studies, and usability tests. Each study aims to gather insights about different aspects of the healthcare industry, such as patient experiences, treatment effectiveness, and product preferences."
  },
  {
    question: "Who can participate in your research?",
    answer: "Participation in healthcare research studies can vary depending on the specific study requirements. Each study will have specific criteria, such as age, gender, or medical history, that must be met to qualify."
  },
  {
    question: "Is my personal information safe and confidential?",
    answer: "Yes, your privacy is our top priority. We use secure data encryption and storage methods to protect your personal information. Any data shared with those who sponsor our research will be anonymized, and your identity will not be revealed to third parties without your explicit consent. If you should ever be in any doubt, please email privacy@medicysltd.co.uk"
  },
  {
    question: "Will I be compensated for participating in a study?",
    answer: "Yes; compensation for participation in our studies may vary depending on the study's nature, duration, and complexity. The specific details of compensation will be outlined in the study description. We pay by your choice of wire transfer, cash cards or eGift cards."
  },
  {
    question: "How do I know if a study is legitimate?",
    answer: "We thoroughly vet all research studies listed on our website to ensure they adhere to ethical research standards. Medicys® are members of BHBIA, EphMRA, ISPOR, ESOMAR, ENcEPP and you can request our credentials at any time by emailing info@medicysltd.co.uk"
  },
  {
    question: "Can I withdraw from a study once I've started?",
    answer: "Yes, you have the right to withdraw from a study at any time without penalty. If you decide to withdraw, please inform the research team as soon as possible so they can make the necessary adjustments."
  },
  {
    question: "How will the results of the study be used?",
    answer: "The results of our studies are used to inform healthcare providers, pharmaceutical companies, and policymakers about patient experiences, treatment effectiveness, and opinions on new products or services. This information is crucial for improving patient care, developing new treatments, and shaping healthcare policies."
  },
  {
    question: "How long do studies typically last?",
    answer: "The duration of a study can vary widely depending on its type and objectives. Some studies, such as online surveys or interviews, may take only a few minutes or hours to complete. Others, like clinical trials or long-term observational studies, may last for several months or even years."
  },
  {
    question: "Can I participate in more than one study at a time?",
    answer: "Yes, you may be eligible to participate in multiple studies simultaneously if the studies do not conflict with each other and you meet the specific eligibility criteria for each study. However, it's essential to manage your time and commitments to ensure you can fully participate in and complete all selected studies."
  },
  {
    question: "Are there any risks involved in participating in your studies?",
    answer: "The risks associated with participating in healthcare studies depend on the nature of the study. Surveys, interviews, and focus groups involve no risk, whereas clinical trials or medical interventions may carry some risks, which will always be clearly outlined in the study's informed consent process. Always carefully review the study details and consult with your healthcare provider if you have concerns about potential risks."
  },
  {
    question: "Will my participation in a study affect my current medical care or treatment?",
    answer: "Your participation in our studies will never interfere with your current medical care or treatment unless the study explicitly involves a change in your treatment plan. It's essential to discuss your participation in a study with your healthcare provider, especially if the study involves medical interventions, treatments, or medications."
  },
  {
    question: "Do I need a referral from my doctor to participate in a study?",
    answer: "In most cases, you do not need a referral from your doctor to participate in our studies. However, some studies may require a doctor's confirmation of diagnosis. Always review the study's eligibility criteria and requirements to determine if this will be the case."
  },
  {
    question: "How are study participants selected?",
    answer: "Study participants are selected based on specific eligibility criteria outlined by the research sponsors. These criteria may include factors such as age, gender, medical history, and geographic location. Researchers typically seek a diverse group of participants to ensure the study results are representative of the broader population."
  },
  {
    question: "Will I receive updates on the study's progress and results?",
    answer: "While few studies provide regular updates or share results with participants, we understand the value of keeping participants informed. You can inquire about the possibility of receiving updates or results during the informed consent process."
  },
  {
    question: "Can I refer friends or family members to participate in studies?",
    answer: "Yes, you can refer friends or family members to participate in our studies, provided they meet the specific eligibility criteria for each study. Encourage them to join our community on our website and complete their profiles to access and express interest in available studies."
  },
  {
    question: "What should I do if I have a concern or complaint about a study?",
    answer: "If you have any concerns or complaints about a study, contact the recruitment team directly to discuss your issue. If your concern is not resolved satisfactorily, you can reach out to our patient engagement director, Joanna Rodriguez (j.rodriguez@medicysltd.co.uk), who will assist you in addressing the matter."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-[var(--light-border)] last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left group"
        data-testid={`faq-item-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="font-['Outfit'] font-semibold text-[var(--navy)] pr-4 group-hover:text-[var(--lime-dark)] transition-colors">
          {question}
        </span>
        <ChevronDown 
          size={20} 
          className={`text-[var(--navy)] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-[var(--text-muted)] leading-relaxed whitespace-pre-line">
          {answer}
        </p>
      </div>
    </div>
  );
};

export const FAQ = ({ limit }) => {
  const [openIndex, setOpenIndex] = useState(0);
  
  const displayedFaqs = limit ? faqData.slice(0, limit) : faqData;

  return (
    <div className="divide-y divide-[var(--light-border)]">
      {displayedFaqs.map((faq, index) => (
        <FAQItem
          key={index}
          question={faq.question}
          answer={faq.answer}
          isOpen={openIndex === index}
          onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
        />
      ))}
    </div>
  );
};

export default FAQ;
